<script lang="ts">
  import { onMount } from "svelte"
  import { Pulse } from "svelte-loading-spinners"
  import { xhr, toast } from "$app/utils"
  import Row from "./PlanMembers/Row.svelte"
  import { ChevronDown, ChevronUp } from "lucide-svelte"
  import { appStore } from "$app/store.svelte"

  let isLoading = $state(true)
  async function initialize(): Promise<void> {
    isLoading = true
    try {
      const resp = await xhr.get(
        `/plans/${appStore.plan.id}/settings/members/member_data`
      )

      let data = resp.data

      appStore.users = appStore.convertUsersHash(data.users)
      appStore.user_roles = data.roles_hash
      appStore.admin = appStore.users.find((pur) => pur.role === "admin")
    } catch (e) {
      console.log(e)
      toast("There was an error loading the member data", "error")
    } finally {
      isLoading = false
    }
  }

  let filteredUsers: (UserType & { edit?: boolean })[] = $state([
    ...appStore.users
  ])
  let txtFilter = $state("")

  /**
   * Filter by name email
   */
  function filterByNameEmail(arr: any[]): any[] {
    if (txtFilter.trim() !== "") {
      const reg = new RegExp(txtFilter, "ig")
      return arr.filter((u) => {
        return reg.test(u.email) || reg.test(u.name)
      })
    } else {
      return arr
    }
  }

  /**
   * Filters all users
   */
  function filterUsers(): void {
    setTimeout(() => {
      filteredUsers = filterByNameEmail([...appStore.users])
      filteredUsers = sortByField(filteredUsers, sortVar.field, sortVar.ord)
    }, 20)
  }

  function sortByField(arr: any, field: string, dir: string): any[] {
    const { a, b } = dir === "desc" ? { a: -1, b: 1 } : { a: 1, b: -1 }

    return arr.sort((va, vb) => (va[field] >= vb[field] ? a : b))
  }

  let sortVar = $state({ ord: "", field: "" })

  function sortBy(field: string): void {
    if (sortVar.field !== field) {
      sortVar.ord = "asc"
    } else {
      sortVar.ord = sortVar.ord === "asc" ? "desc" : "asc"
    }
    sortVar.field = field

    filteredUsers = sortByField(filteredUsers, field, sortVar.ord)
  }

  sortBy("name")

  onMount(async () => {
    initialize()
  })
  $effect(() => {
    if (txtFilter.length === 0) {
      filteredUsers = sortByField(
        [...appStore.users],
        sortVar.field,
        sortVar.ord
      )
    }
  })
</script>

<div class="max-w-4xl mx-auto py-6">
  <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
    <div class="flex justify-start items-center gap-x-4">
      <h3 class="text-xl font-bold">Plan Members</h3>
      <div class="relative">
        <div
          class="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center w-full"
        >
          <!-- Heroicon name: solid/search -->
          <svg
            class="h-5 w-5 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fill-rule="evenodd"
              d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <input
          class="block w-full sm:w-72 bg-white border border-gray-300 rounded-md py-2 pl-10 pr-3 text-sm placeholder-gray-500 focus:outline-none focus:text-gray-900 focus:placeholder-gray-400 focus:ring-1 focus:ring-rose-500 focus:border-rose-500 sm:text-sm"
          placeholder="Type name or email to filter"
          bind:value={txtFilter}
          onkeyup={filterUsers}
          type="search"
        />
      </div>
    </div>
    {#if appStore.current_user.role === "admin"}
      <div class="flex justify-end items-center">
        <a
          href="/plans/{appStore.plan.id}/admin#members"
          class="text-blue-500 text-sm px-2 py-1 border border-blue-500 rounded-md hover:bg-blue-500 hover:text-white"
        >
          Add or Remove Members</a
        >
      </div>
    {/if}
  </div>

  <div class="py-6">
    <div class="flex flex-col">
      {#if isLoading}
        <div class="py-12 flex justify-center w-full">
          <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
        </div>
      {:else}
        <div class="-my-2 sm:-mx-6 lg:-mx-8">
          <div
            class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8"
          >
            <div class="shadow-md w-full relative">
              <div class="grid grid-cols-12 thead w-full sticky" style="top:0;">
                <!-- svelte-ignore a11y_click_events_have_key_events -->
                <div
                  class="col-span-3 cursor-pointer flex items-center gap-1"
                  onclick={() => sortBy("name")}
                  role="button"
                  tabindex="0"
                >
                  <span class:hidden={sortVar.field !== "name"}>
                    {#if sortVar.ord === "asc"}
                      <ChevronUp />
                    {:else}
                      <ChevronDown />
                    {/if}
                  </span>
                  Name
                </div>
                <!-- svelte-ignore a11y_positive_tabindex -->
                <!-- svelte-ignore a11y_click_events_have_key_events -->
                <div
                  class="col-span-3 cursor-pointer flex items-center gap-1"
                  onclick={() => sortBy("email")}
                  role="button"
                  tabindex="1"
                >
                  <span class:hidden={sortVar.field !== "email"}>
                    {#if sortVar.ord === "asc"}
                      <ChevronUp />
                    {:else}
                      <ChevronDown />
                    {/if}
                  </span>
                  Email Address
                </div>
                <div class="col-span-2 flex items-center gap-1">Role</div>
                <!-- svelte-ignore a11y_positive_tabindex -->
                <!-- svelte-ignore a11y_click_events_have_key_events -->
                <div
                  class="col-span-2 cursor-pointer flex items-center gap-1"
                  onclick={() => sortBy("role")}
                  role="button"
                  tabindex="3"
                >
                  <span class:hidden={sortVar.field !== "role"}>
                    {#if sortVar.ord === "asc"}
                      <ChevronUp />
                    {:else}
                      <ChevronDown />
                    {/if}
                  </span>
                  Permission
                </div>
                <div class="col-span-2"></div>
              </div>

              {#each filteredUsers as user (user.id)}
                <div class="border-b border-gray-200">
                  <Row {user} />
                </div>
              {/each}
            </div>
          </div>
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  .thead > div {
    display: flex;
  }
</style>
